import { supabase } from '../../integrations/supabase/client';
import type {
  SupplierOnboardingFlow,
  SupplierRoleAssignment,
  CreateSupplierFlowData,
  CompleteSupplierStepData,
  SupplierAnalytics,
  SupplierTypeBreakdown,
  SupplierRiskBreakdown,
  ISupplierRepository,
} from '../interfaces/ISupplierRepository';

export class SupabaseSupplierRepository implements ISupplierRepository {
  async getSupplierFlowByToken(token: string): Promise<SupplierOnboardingFlow | null> {
    const { data, error } = await supabase
      .from('onboarding_flows')
      .select('*')
      .eq('magic_token', token)
      .eq('is_supplier_flow', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as unknown as SupplierOnboardingFlow;
  }

  async getSupplierFlowById(flowId: string): Promise<SupplierOnboardingFlow | null> {
    const { data, error } = await supabase
      .from('onboarding_flows')
      .select('*')
      .eq('id', flowId)
      .eq('is_supplier_flow', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as unknown as SupplierOnboardingFlow;
  }

  async getAllSupplierFlows(platformTenantId?: string): Promise<SupplierOnboardingFlow[]> {
    let query = supabase
      .from('onboarding_flows')
      .select('*')
      .eq('is_supplier_flow', true)
      .order('created_at', { ascending: false });

    if (platformTenantId) {
      query = query.eq('platform_tenant_id', platformTenantId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data as unknown as SupplierOnboardingFlow[]) || [];
  }

  async getSupplierRoleAssignments(flowId: string): Promise<SupplierRoleAssignment[]> {
    const { data, error } = await supabase
      .from('onboarding_flow_role_assignments')
      .select(`
        *,
        role_template:role_templates(role_code, display_name, category)
      `)
      .eq('onboarding_flow_id', flowId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data as unknown as SupplierRoleAssignment[]) || [];
  }

  async getRoleAssignmentById(assignmentId: string): Promise<SupplierRoleAssignment | null> {
    const { data, error } = await supabase
      .from('onboarding_flow_role_assignments')
      .select(`
        *,
        role_template:role_templates(role_code, display_name, category)
      `)
      .eq('id', assignmentId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data as unknown as SupplierRoleAssignment;
  }

  async createSupplierFlow(data: CreateSupplierFlowData): Promise<SupplierOnboardingFlow> {
    const { data: result, error } = await supabase.functions.invoke('create-supplier-onboarding', {
      body: data,
    });

    if (error) throw error;
    if (!result.success) throw new Error(result.error || 'Failed to create supplier onboarding flow');

    // Fetch the created flow
    const flow = await this.getSupplierFlowById(result.onboardingFlowId);
    if (!flow) throw new Error('Failed to fetch created supplier flow');

    return flow;
  }

  async completeSupplierStep(data: CompleteSupplierStepData): Promise<void> {
    const { data: result, error } = await supabase.functions.invoke('complete-supplier-step', {
      body: data,
    });

    if (error) throw error;
    if (!result.success) throw new Error(result.error || 'Failed to complete supplier step');
  }

  async updateSupplierFlowStatus(
    flowId: string,
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  ): Promise<void> {
    const { error } = await supabase
      .from('onboarding_flows')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', flowId)
      .eq('is_supplier_flow', true);

    if (error) throw error;
  }

  async getSupplierAnalytics(): Promise<SupplierAnalytics> {
    const { data, error } = await supabase.rpc('get_supplier_analytics_metrics');

    if (error) throw error;

    return {
      totalFlows: Number(data[0]?.total_flows || 0),
      completedFlows: Number(data[0]?.completed_flows || 0),
      pendingFlows: Number(data[0]?.pending_flows || 0),
      failedFlows: Number(data[0]?.failed_flows || 0),
      avgCompletionTimeDays: Number(data[0]?.avg_completion_time_days || 0),
      completionRate: Number(data[0]?.completion_rate || 0),
    };
  }

  async getSupplierTypeBreakdown(): Promise<SupplierTypeBreakdown[]> {
    const { data, error } = await supabase.rpc('get_supplier_type_breakdown');

    if (error) throw error;

    return data.map((item: any) => ({
      supplierType: item.supplier_type,
      count: Number(item.count),
    }));
  }

  async getSupplierRiskBreakdown(): Promise<SupplierRiskBreakdown[]> {
    const { data, error } = await supabase.rpc('get_supplier_risk_breakdown');

    if (error) throw error;

    return data.map((item: any) => ({
      riskLevel: item.risk_level,
      count: Number(item.count),
    }));
  }

  // ========================================
  // SUPPLIER ENTITY MANAGEMENT (NEW)
  // ========================================

  /**
   * Get all suppliers with optional filters
   */
  async getSuppliers(filters?: {
    clientId?: string;
    platformTenantId?: string;
    status?: string;
    supplierType?: string;
    riskLevel?: string;
    search?: string;
  }): Promise<any[]> {
    let query = (supabase as any)
      .from('suppliers')
      .select(`
        *,
        client:clients(id, name, tenant_type),
        relationships:client_supplier_relationships(*)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (filters?.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    if (filters?.platformTenantId) {
      query = query.eq('platform_tenant_id', filters.platformTenantId);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.supplierType) {
      query = query.eq('supplier_type', filters.supplierType);
    }

    if (filters?.riskLevel) {
      query = query.eq('risk_level', filters.riskLevel);
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,primary_email.ilike.%${filters.search}%,legal_name.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  }

  /**
   * Get supplier by ID
   */
  async getSupplierById(supplierId: string): Promise<any | null> {
    const { data, error } = await (supabase as any)
      .from('suppliers')
      .select(`
        *,
        client:clients(id, name, tenant_type, primary_contact_email),
        relationships:client_supplier_relationships(*),
        bank_details:supplier_bank_details(*),
        compliance_checks:supplier_compliance_checks(*),
        risk_factors:supplier_risk_factors(*)
      `)
      .eq('id', supplierId)
      .is('deleted_at', null)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  /**
   * Create new supplier
   */
  async createSupplier(supplierData: any): Promise<any> {
    // Check for duplicate email within same platform tenant
    if (supplierData.primaryEmail && supplierData.platformTenantId) {
      const { data: existingByEmail } = await (supabase as any)
        .from('suppliers')
        .select('id, name')
        .eq('platform_tenant_id', supplierData.platformTenantId)
        .ilike('primary_email', supplierData.primaryEmail)
        .is('deleted_at', null)
        .limit(1);

      if (existingByEmail && existingByEmail.length > 0) {
        throw new Error(`A supplier with email "${supplierData.primaryEmail}" already exists: "${existingByEmail[0].name}"`);
      }
    }

    // Check for duplicate VAT number within same platform tenant
    if (supplierData.vatNumber && supplierData.platformTenantId) {
      const { data: existingByVat } = await (supabase as any)
        .from('suppliers')
        .select('id, name')
        .eq('platform_tenant_id', supplierData.platformTenantId)
        .ilike('vat_number', supplierData.vatNumber)
        .is('deleted_at', null)
        .limit(1);

      if (existingByVat && existingByVat.length > 0) {
        throw new Error(`A supplier with VAT number "${supplierData.vatNumber}" already exists: "${existingByVat[0].name}"`);
      }
    }

    // Check for duplicate tax ID within same platform tenant
    if (supplierData.taxId && supplierData.platformTenantId) {
      const { data: existingByTaxId } = await (supabase as any)
        .from('suppliers')
        .select('id, name')
        .eq('platform_tenant_id', supplierData.platformTenantId)
        .ilike('tax_id', supplierData.taxId)
        .is('deleted_at', null)
        .limit(1);

      if (existingByTaxId && existingByTaxId.length > 0) {
        throw new Error(`A supplier with Tax ID "${supplierData.taxId}" already exists: "${existingByTaxId[0].name}"`);
      }
    }

    const { data, error } = await (supabase as any)
      .from('suppliers')
      .insert({
        client_id: supplierData.clientId,
        platform_tenant_id: supplierData.platformTenantId,
        name: supplierData.name,
        supplier_type: supplierData.supplierType,
        legal_name: supplierData.legalName,
        tax_id: supplierData.taxId,
        vat_number: supplierData.vatNumber,
        primary_email: supplierData.primaryEmail,
        primary_phone: supplierData.primaryPhone,
        website: supplierData.website,
        street: supplierData.street,
        city: supplierData.city,
        postal_code: supplierData.postalCode,
        country: supplierData.country,
        status: supplierData.status || 'pending_approval',
        payment_terms: supplierData.paymentTerms,
        payment_method: supplierData.paymentMethod,
        currency: supplierData.currency || 'EUR',
        category: supplierData.category,
        custom_fields: supplierData.customFields || {},
        created_by: supplierData.createdBy,
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Update supplier
   */
  async updateSupplier(supplierId: string, updates: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('suppliers')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
        updated_by: updates.updatedBy,
      })
      .eq('id', supplierId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Approve supplier
   */
  async approveSupplier(supplierId: string, approvedBy: string, notes?: string): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('suppliers')
      .update({
        status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date().toISOString(),
        approval_notes: notes,
        updated_at: new Date().toISOString(),
      })
      .eq('id', supplierId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Change supplier status
   */
  async updateSupplierStatus(supplierId: string, status: string, updatedBy: string): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('suppliers')
      .update({
        status,
        updated_at: new Date().toISOString(),
        updated_by: updatedBy,
      })
      .eq('id', supplierId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Soft delete supplier
   */
  async deleteSupplier(supplierId: string, deletedBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('suppliers')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy,
      })
      .eq('id', supplierId);

    if (error) throw error;
  }

  /**
   * Get suppliers by client
   */
  async getSuppliersByClient(clientId: string): Promise<any[]> {
    return this.getSuppliers({ clientId });
  }

  /**
   * Get suppliers pending approval
   */
  async getSuppliersPendingApproval(platformTenantId?: string): Promise<any[]> {
    return this.getSuppliers({
      status: 'pending_approval',
      platformTenantId,
    });
  }

  /**
   * Search suppliers
   */
  async searchSuppliers(searchTerm: string, platformTenantId?: string): Promise<any[]> {
    return this.getSuppliers({
      search: searchTerm,
      platformTenantId,
    });
  }

  /**
   * Get supplier statistics
   */
  async getSupplierStats(platformTenantId?: string): Promise<{
    total: number;
    active: number;
    pendingApproval: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    byRiskLevel: Record<string, number>;
  }> {
    // Use parallel count queries instead of fetching all records
    const buildQuery = () => {
      let q = (supabase as any).from('suppliers').select('*', { count: 'exact', head: true }).is('deleted_at', null);
      if (platformTenantId) q = q.eq('platform_tenant_id', platformTenantId);
      return q;
    };

    const [
      { count: total },
      { count: active },
      { count: pendingApproval },
    ] = await Promise.all([
      buildQuery(),
      buildQuery().eq('status', 'active'),
      buildQuery().eq('status', 'pending_approval'),
    ]);

    // Note: byStatus, byType, byRiskLevel would require fetching data or additional queries
    // For now, return empty objects for detailed breakdowns to avoid fetching all records
    // These can be populated on-demand when the user drills down
    return {
      total: total || 0,
      active: active || 0,
      pendingApproval: pendingApproval || 0,
      byStatus: {},
      byType: {},
      byRiskLevel: {},
    };
  }

  /**
   * Create client-supplier relationship
   */
  async createRelationship(relationshipData: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('client_supplier_relationships')
      .insert({
        client_id: relationshipData.clientId,
        supplier_id: relationshipData.supplierId,
        platform_tenant_id: relationshipData.platformTenantId,
        relationship_type: relationshipData.relationshipType || 'standard',
        status: relationshipData.status || 'active',
        payment_terms: relationshipData.paymentTerms,
        payment_method: relationshipData.paymentMethod,
        currency: relationshipData.currency || 'EUR',
        credit_limit: relationshipData.creditLimit,
        created_by: relationshipData.createdBy,
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Update relationship
   */
  async updateRelationship(relationshipId: string, updates: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('client_supplier_relationships')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', relationshipId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Get relationships for supplier
   */
  async getSupplierRelationships(supplierId: string): Promise<any[]> {
    const { data, error } = await (supabase as any)
      .from('client_supplier_relationships')
      .select(`
        *,
        client:clients(id, name, tenant_type)
      `)
      .eq('supplier_id', supplierId);

    if (error) throw error;

    return data || [];
  }

  // ========================================
  // SUPPLIER CATEGORIES
  // ========================================

  /**
   * Get categories for supplier
   */
  async getSupplierCategories(supplierId: string): Promise<any[]> {
    const { data, error} = await (supabase as any)
      .from('supplier_categories')
      .select('*')
      .eq('supplier_id', supplierId)
      .is('deleted_at', null)
      .order('is_primary', { ascending: false })
      .order('category_name', { ascending: true });

    if (error) throw error;

    return data || [];
  }

  /**
   * Create supplier category
   */
  async createSupplierCategory(categoryData: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('supplier_categories')
      .insert({
        supplier_id: categoryData.supplierId,
        platform_tenant_id: categoryData.platformTenantId,
        category_name: categoryData.categoryName,
        sub_category: categoryData.subCategory,
        description: categoryData.description,
        is_primary: categoryData.isPrimary || false,
        certification_level: categoryData.certificationLevel,
        annual_spend: categoryData.annualSpend,
        currency: categoryData.currency || 'EUR',
        capabilities: categoryData.capabilities || [],
        certifications: categoryData.certifications || [],
        custom_fields: categoryData.customFields || {},
        notes: categoryData.notes,
        created_by: categoryData.createdBy,
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Update supplier category
   */
  async updateSupplierCategory(categoryId: string, updates: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('supplier_categories')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', categoryId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Delete supplier category
   */
  async deleteSupplierCategory(categoryId: string, deletedBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('supplier_categories')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy,
      })
      .eq('id', categoryId);

    if (error) throw error;
  }

  // ========================================
  // SUPPLIER CONTACTS
  // ========================================

  /**
   * Get contacts for supplier
   */
  async getSupplierContacts(supplierId: string): Promise<any[]> {
    const { data, error } = await (supabase as any)
      .from('organization_contacts')
      .select('*')
      .eq('supplier_id', supplierId)
      .is('deleted_at', null)
      .order('is_primary', { ascending: false })
      .order('contact_type', { ascending: true })
      .order('last_name', { ascending: true });

    if (error) throw error;

    return data || [];
  }

  /**
   * Create supplier contact
   */
  async createSupplierContact(contactData: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('organization_contacts')
      .insert({
        supplier_id: contactData.supplierId,
        platform_tenant_id: contactData.platformTenantId,
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        title: contactData.title,
        department: contactData.department,
        email: contactData.email,
        phone: contactData.phone,
        mobile: contactData.mobile,
        contact_type: contactData.contactType,
        is_primary: contactData.isPrimary || false,
        preferred_contact_method: contactData.preferredContactMethod,
        language: contactData.language || 'en',
        timezone: contactData.timezone,
        available_hours: contactData.availableHours,
        notes: contactData.notes,
        status: contactData.status || 'active',
        custom_fields: contactData.customFields || {},
        created_by: contactData.createdBy,
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Update supplier contact
   */
  async updateSupplierContact(contactId: string, updates: any): Promise<any> {
    const { data, error } = await (supabase as any)
      .from('organization_contacts')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', contactId)
      .select()
      .single();

    if (error) throw error;

    return data;
  }

  /**
   * Delete supplier contact
   */
  async deleteSupplierContact(contactId: string, deletedBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('organization_contacts')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy,
      })
      .eq('id', contactId);

    if (error) throw error;
  }
}
